import {Injectable} from '@angular/core';
import {HttpHeaders} from '@angular/common/http';
import {ApiCollection, ApiResponse, RiskControlCategoryRow, RiskControlDetail, RiskControlRow} from '@core/types';
import {ENDPOINTS, GET_URL, log} from '@core/utils';
import {HttpService} from '@core/services';
import {CompanyStateService} from '@core/states';
import {AuthStateService} from '@auth/states';

@Injectable({
  providedIn: 'root'
})
export class ControlService {
  constructor(
    private apiService: HttpService,
    private companyState: CompanyStateService,
    private authState: AuthStateService
  ) {}

  async getControls(page = 1, itemsPerPage = 50, options?: {skipLoader?: boolean}): Promise<ApiResponse<RiskControlRow[]>> {
    try {
      const url = GET_URL(ENDPOINTS.CONTROLS, {page, itemsPerPage});
      const headers = this.buildHeaders(options?.skipLoader);
      const response: ApiCollection<any> = await this.apiService.get(url, headers ? {headers} : undefined);
      const controls = this.apiService.normalizeCollection<any>(response).member.map((item: any) => this.mapControl(item));
      return {success: true, result: controls};
    } catch (error: any) {
      log.error('Error loading controls:', error);
    }
    return {success: false, message: 'No se pudieron cargar los controles.'};
  }

  async getControl(controlId: string | number, options?: {skipLoader?: boolean}): Promise<ApiResponse<RiskControlDetail>> {
    try {
      const url = GET_URL(ENDPOINTS.CONTROL_DETAIL, {id: controlId});
      const headers = this.buildHeaders(options?.skipLoader);
      const response: any = await this.apiService.get(url, headers ? {headers} : undefined);
      return {success: true, result: this.mapControlDetail(response)};
    } catch (error: any) {
      log.error('Error loading control detail:', error);
    }
    return {success: false, message: 'No se pudo cargar el control.'};
  }

  async createControl(payload: {
    name: string;
    description?: string;
    objectives?: string;
    type?: string;
    declaredWeight?: string | number | null;
    dependency?: string;
    validatorId?: string | number;
    taskId?: string | number;
    policyId?: string | number | null;
    courseId?: string | number | null;
    categoryId?: string | number | null;
    conceptIds?: Array<string | number>;
    conceptWeights?: Record<string, number>;
    variableIds?: Array<string | number>;
    affectsType?: string;
    status?: string;
  }, options?: {skipLoader?: boolean}): Promise<ApiResponse<RiskControlRow>> {
    try {
      const url = GET_URL(ENDPOINTS.CONTROLS);
      const headers = this.buildHeaders(options?.skipLoader);
      const response: any = await this.apiService.post(url, this.mapControlPayload(payload, {sparse: false}), headers ? {headers} : undefined);
      return {success: true, result: this.mapControl(response)};
    } catch (error: any) {
      log.error('Error creating control:', error);
    }
    return {success: false, message: 'No se pudo crear el control.'};
  }

  async updateControl(controlId: string | number, payload: {
    name?: string;
    description?: string;
    objectives?: string;
    type?: string;
    declaredWeight?: string | number | null;
    dependency?: string;
    validatorId?: string | number;
    taskId?: string | number;
    policyId?: string | number | null;
    courseId?: string | number | null;
    categoryId?: string | number | null;
    conceptIds?: Array<string | number>;
    conceptWeights?: Record<string, number>;
    variableIds?: Array<string | number>;
    affectsType?: string;
    status?: string;
  }, options?: {skipLoader?: boolean}): Promise<ApiResponse<RiskControlRow>> {
    try {
      const url = GET_URL(ENDPOINTS.CONTROL_DETAIL, {id: controlId});
      const baseHeaders = this.buildHeaders(options?.skipLoader) ?? new HttpHeaders();
      const headers = baseHeaders.set('Content-Type', 'application/merge-patch+json');
      const response: any = await this.apiService.patch(url, this.mapControlPayload(payload, {sparse: true}), {headers, json: false});
      if (!response) {
        return {success: true};
      }
      return {success: true, result: this.mapControl(response)};
    } catch (error: any) {
      log.error('Error updating control:', error);
    }
    return {success: false, message: 'No se pudo actualizar el control.'};
  }

  async updateStatus(
    controlId: string | number,
    status: 'draft' | 'in_execution' | 'finished' | 'permanent' | 'discarded',
    options?: {skipLoader?: boolean}
  ): Promise<ApiResponse<undefined>> {
    try {
      const url = GET_URL(ENDPOINTS.CONTROL_STATUS, {id: controlId});
      const baseHeaders = this.buildHeaders(options?.skipLoader) ?? new HttpHeaders();
      const headers = baseHeaders.set('Content-Type', 'application/merge-patch+json');
      await this.apiService.patch(url, {status}, {headers, json: false});
      return {success: true};
    } catch (error: any) {
      log.error('Error updating control status:', error);
    }
    return {success: false, message: 'No se pudo actualizar el estado.'};
  }

  async deleteControl(controlId: string | number, options?: {skipLoader?: boolean}): Promise<ApiResponse<undefined>> {
    try {
      const url = GET_URL(ENDPOINTS.CONTROL_DETAIL, {id: controlId});
      const headers = this.buildHeaders(options?.skipLoader);
      await this.apiService.delete(url, headers ? {headers} : undefined);
      return {success: true};
    } catch (error: any) {
      log.error('Error deleting control:', error);
    }
    return {success: false, message: 'No se pudo eliminar el control.'};
  }

  async evaluateControl(
    controlId: string | number,
    payload: {result: 'satisfactory' | 'satisfactory_with_recommendations' | 'unsatisfactory'; justification: string},
    options?: {skipLoader?: boolean}
  ): Promise<ApiResponse<RiskControlDetail>> {
    try {
      const url = GET_URL(ENDPOINTS.CONTROL_EVALUATE, {id: controlId});
      const baseHeaders = this.buildHeaders(options?.skipLoader) ?? new HttpHeaders();
      const headers = baseHeaders.set('Content-Type', 'application/merge-patch+json');
      const response: any = await this.apiService.patch(url, payload, {headers, json: false});
      return {success: true, result: this.mapControlDetail(response)};
    } catch (error: any) {
      log.error('Error evaluating control:', error);
    }
    return {success: false, message: 'No se pudo registrar la evaluación del control.'};
  }

  async getCategories(page = 1, itemsPerPage = 100, options?: {skipLoader?: boolean}): Promise<ApiResponse<RiskControlCategoryRow[]>> {
    try {
      const url = GET_URL(ENDPOINTS.CONTROL_CATEGORIES, {page, itemsPerPage});
      const headers = this.buildHeaders(options?.skipLoader);
      const response: ApiCollection<any> = await this.apiService.get(url, headers ? {headers} : undefined);
      const categories = this.apiService.normalizeCollection<any>(response).member.map((item: any) => ({
        id: item.id,
        name: item.name || 'Sin nombre',
        description: item.description || ''
      }));
      return {success: true, result: categories};
    } catch (error: any) {
      log.error('Error loading control categories:', error);
    }
    return {success: false, message: 'No se pudieron cargar las categorías de control.'};
  }

  async createCategory(payload: {name: string; description?: string}, options?: {skipLoader?: boolean}): Promise<ApiResponse<RiskControlCategoryRow>> {
    try {
      const url = GET_URL(ENDPOINTS.CONTROL_CATEGORIES);
      const headers = this.buildHeaders(options?.skipLoader);
      const response: any = await this.apiService.post(url, payload, headers ? {headers} : undefined);
      return {success: true, result: {id: response.id, name: response.name || 'Sin nombre', description: response.description || ''}};
    } catch (error: any) {
      log.error('Error creating control category:', error);
    }
    return {success: false, message: 'No se pudo crear la categoría.'};
  }

  async updateCategory(categoryId: string | number, payload: {name?: string; description?: string}, options?: {skipLoader?: boolean}): Promise<ApiResponse<RiskControlCategoryRow>> {
    try {
      const url = GET_URL(ENDPOINTS.CONTROL_CATEGORY_DETAIL, {id: categoryId});
      const baseHeaders = this.buildHeaders(options?.skipLoader) ?? new HttpHeaders();
      const headers = baseHeaders.set('Content-Type', 'application/merge-patch+json');
      const response: any = await this.apiService.patch(url, payload, {headers, json: false});
      if (!response) return {success: true};
      return {success: true, result: {id: response.id, name: response.name || 'Sin nombre', description: response.description || ''}};
    } catch (error: any) {
      log.error('Error updating control category:', error);
    }
    return {success: false, message: 'No se pudo actualizar la categoría.'};
  }

  async deleteCategory(categoryId: string | number, options?: {skipLoader?: boolean}): Promise<ApiResponse<undefined>> {
    try {
      const url = GET_URL(ENDPOINTS.CONTROL_CATEGORY_DETAIL, {id: categoryId});
      const headers = this.buildHeaders(options?.skipLoader);
      await this.apiService.delete(url, headers ? {headers} : undefined);
      return {success: true};
    } catch (error: any) {
      log.error('Error deleting control category:', error);
    }
    return {success: false, message: 'No se pudo eliminar la categoría.'};
  }

  private mapControl(item: any): RiskControlRow {
    return {
      id: item.id,
      name: item.name || item.title || 'Sin nombre',
      description: item.description || '',
      categoryId: item.category?.id || item.categoryId || undefined,
      categoryName: item.category?.name || item.categoryName || '',
      planId: item.plan?.id || item.planId || undefined,
      status: this.normalizeStatus(item.status || item.state || 'draft'),
      type: this.normalizeType(item.type || item.kind || ''),
      declaredWeight: item.declaredWeight ?? item.declared_weight ?? '',
      evaluationFactor: item.evaluationFactor || item.evaluation_factor || undefined,
      evaluationResult: item.evaluationResult || item.evaluation_result || undefined,
      conceptWeights: this.normalizeConceptWeights(item.conceptWeights ?? item.concept_weights),
      dependency: item.dependency || '',
      affects: this.mapAffects(item),
      task: item.task?.name || item.taskName || '',
      taskId: item.task?.id || item.taskId || undefined,
      riskLevel: item.riskLevel || item.risk || '',
      variableIds: Array.isArray(item.variables)
        ? item.variables.map((entry: any) => entry?.id).filter((id: any) => !!id)
        : []
    };
  }

  private mapControlDetail(item: any): RiskControlDetail {
    const base = this.mapControl(item);
    const policyId = this.extractRelatedId(item.policy?.id ?? item.policyId ?? item.policy);
    const courseId = this.extractRelatedId(item.course?.id ?? item.courseId ?? item.course);
    const categoryId = this.extractRelatedId(item.category?.id ?? item.categoryId ?? item.category);
    const categoryName = this.extractRelatedName(
      item.category,
      item.categoryName,
      item.category_name,
      item.categoryTitle,
      item.categoryLabel
    );
    const policyName = this.extractRelatedName(
      item.policy,
      item.policyName,
      item.policy_name,
      item.policyTitle,
      item.policyLabel
    );
    const courseName = this.extractRelatedName(
      item.course,
      item.courseName,
      item.course_name,
      item.courseTitle,
      item.courseLabel
    );
    return {
      ...base,
      objectives: item.objectives || '',
      validator: item.validator?.name || item.validatorName || '',
      validatorId: item.validator?.id || item.validatorId || undefined,
      taskId: item.task?.id || item.taskId || undefined,
      planId: item.plan?.id || item.planId || undefined,
      policyId: policyId ?? undefined,
      policyName: policyName || undefined,
      courseId: courseId ?? undefined,
      courseName: courseName || undefined,
      categoryId: categoryId ?? undefined,
      categoryName: categoryName || undefined,
      conceptIds: Array.isArray(item.concepts)
        ? item.concepts.map((entry: any) => entry?.id).filter((id: any) => !!id)
        : [],
      conceptWeights: this.normalizeConceptWeights(item.conceptWeights ?? item.concept_weights),
      evaluationResult: item.evaluationResult || item.evaluation_result || undefined,
      evaluationFactor: item.evaluationFactor || item.evaluation_factor || undefined,
      evaluationJustification: item.evaluationJustification || item.evaluation_justification || undefined,
      evaluatedAt: item.evaluatedAt || item.evaluated_at || undefined,
      variableIds: Array.isArray(item.variables)
        ? item.variables.map((entry: any) => entry?.id).filter((id: any) => !!id)
        : [],
      contributions: Array.isArray(item.contributions)
        ? item.contributions.map((entry: any) => ({
            type: entry.type || entry.label || '',
            individual: entry.individual || entry.individualValue || '',
            controlled: entry.controlled || entry.controlledValue || ''
          }))
        : [],
      attachments: Array.isArray(item.attachments || item.attachmentSummaries || item.documents || item.files)
        ? (item.attachments || item.attachmentSummaries || item.documents || item.files).map((entry: any) => ({
            '@context': entry?.['@context'] || '',
            '@id': entry?.['@id'] || '',
            '@type': entry?.['@type'] || '',
            id: entry?.id,
            fileName: entry?.fileName || entry?.name || 'Documento',
            filePath: entry?.filePath || entry?.path,
            mimeType: entry?.mimeType || entry?.type,
            fileSize: entry?.fileSize || entry?.size || entry?.bytes,
            createdAt: entry?.createdAt || entry?.created_at,
            uploadedById: entry?.uploadedById,
            uploadedByType: entry?.uploadedByType
          }))
        : []
    };
  }

  private extractRelatedId(value: any): string | number | null {
    if (value === undefined || value === null) return null;
    if (typeof value === 'number') return value;
    if (typeof value === 'object') {
      const nested = value.id ?? value['@id'];
      return this.extractRelatedId(nested);
    }
    const raw = String(value).trim();
    if (!raw) return null;
    if (/^\d+$/.test(raw)) return raw;
    if (/^[0-9a-fA-F-]{8,}$/.test(raw)) return raw;
    const iriMatch = raw.match(/\/([^/]+)(?:\/)?$/);
    return iriMatch ? iriMatch[1] : null;
  }

  private extractRelatedName(...values: any[]): string {
    for (const value of values) {
      if (!value) continue;
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed && !trimmed.startsWith('/api/')) return trimmed;
        continue;
      }
      if (typeof value === 'object') {
        const name = value.name || value.title || value.label;
        if (name && String(name).trim()) return String(name).trim();
      }
    }
    return '';
  }

  private mapAffects(item: any): string {
    const conceptNames = this.mapRelatedNames(item?.concepts);
    if (conceptNames.length) {
      return `Conceptos: ${conceptNames.join(', ')}`;
    }
    const affects = item?.affects;
    if (Array.isArray(affects)) {
      const names = affects
        .map((entry: any) => entry?.name || entry?.label || '')
        .filter((name: string) => !!name);
      return names.join(', ');
    }
    return affects || item?.affectsType || '';
  }

  private mapRelatedNames(list: any): string[] {
    if (!Array.isArray(list)) return [];
    return list
      .map((entry: any) => entry?.name || entry?.label || '')
      .filter((name: string) => !!name);
  }

  private mapControlPayload(payload: {
    name?: string;
    description?: string;
    objectives?: string;
    type?: string;
    declaredWeight?: string | number | null;
    dependency?: string;
    validatorId?: string | number;
    taskId?: string | number;
    policyId?: string | number | null;
    courseId?: string | number | null;
    categoryId?: string | number | null;
    conceptIds?: Array<string | number>;
    conceptWeights?: Record<string, number>;
    variableIds?: Array<string | number>;
    affectsType?: string;
    status?: string;
  }, options?: {sparse?: boolean}): Record<string, any> {
    const sparse = !!options?.sparse;
    const output: Record<string, any> = {};

    if (!sparse || payload.name !== undefined) {
      output['name'] = payload.name;
    }
    if (!sparse || payload.description !== undefined) {
      output['description'] = payload.description;
    }
    if (!sparse || payload.objectives !== undefined) {
      output['objectives'] = sparse ? payload.objectives : (payload.objectives ?? '');
    }
    if (!sparse || payload.type !== undefined) {
      output['type'] = this.normalizeType(payload.type || 'declarative');
    }
    if (!sparse || payload.declaredWeight !== undefined) {
      output['declaredWeight'] = payload.declaredWeight !== undefined && payload.declaredWeight !== null && String(payload.declaredWeight).trim() !== ''
        ? String(payload.declaredWeight)
        : null;
    }
    if (!sparse || payload.dependency !== undefined) {
      output['dependency'] = payload.dependency || 'approval';
    }
    if (!sparse || payload.status !== undefined) {
      output['status'] = this.normalizeStatus(payload.status || 'draft');
    }

    if (payload.validatorId) {
      output['validatorUser'] = this.toResourceIri(payload.validatorId, 'users');
    }
    if (payload.taskId) {
      output['task'] = this.toResourceIri(payload.taskId, 'tasks');
    }
    if (Array.isArray(payload.conceptIds)) {
      output['concepts'] = payload.conceptIds.map(id => this.toResourceIri(id, 'risk/concepts'));
    }
    if (payload.conceptWeights !== undefined) {
      output['conceptWeights'] = payload.conceptWeights || {};
    }
    if (payload['policyId'] !== undefined) {
      output['policy'] = payload['policyId'] ? this.toResourceIri(payload['policyId'], 'policies') : null;
    }
    if (payload['courseId'] !== undefined) {
      output['course'] = payload['courseId'] ? this.toResourceIri(payload['courseId'], 'courses') : null;
    }
    if (payload['categoryId'] !== undefined) {
      output['category'] = payload['categoryId'] ? this.toResourceIri(payload['categoryId'], 'risk/control-categories') : null;
    }
    if (Array.isArray(payload.variableIds)) {
      output['variables'] = payload.variableIds.map(id => this.toResourceIri(id, 'risk/variables'));
    }

    return output;
  }

  private toResourceIri(id: string | number, collection: string): string | number {
    if (typeof id === 'string' && id.startsWith('/api/')) {
      return id;
    }
    return `/api/v1/${collection}/${id}`;
  }

  private buildHeaders(skipLoader = false): HttpHeaders | null {
    if (this.authState.isAdmin()) {
      if (!skipLoader) return null;
      return new HttpHeaders().set('X-Skip-Loader', '1');
    }

    const companyId = this.companyState.membership()?.company?.id;
    if (!companyId) {
      if (!skipLoader) return null;
      return new HttpHeaders().set('X-Skip-Loader', '1');
    }

    let headers = new HttpHeaders().set('X-Selected-Company-ID', String(companyId));
    if (skipLoader) {
      headers = headers.set('X-Skip-Loader', '1');
    }
    return headers;
  }

  private normalizeType(value: string): string {
    const normalized = String(value || '').trim().toLowerCase();
    if (normalized === 'declarative' || normalized === 'policy' || normalized === 'course') {
      return normalized;
    }
    return 'declarative';
  }

  private normalizeStatus(value: string): string {
    const normalized = String(value || '').trim().toLowerCase();
    if (normalized === 'evaluation') return 'draft';
    if (normalized === 'approved') return 'in_execution';
    if (normalized === 'rejected') return 'discarded';
    if (normalized === 'approval_process' || normalized === 'pending_approval') return 'in_execution';
    return normalized || 'draft';
  }

  private normalizeConceptWeights(value: unknown): Record<string, number> {
    if (!value || typeof value !== 'object') return {};
    const output: Record<string, number> = {};
    for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
      const parsed = Number(raw);
      if (!Number.isFinite(parsed)) continue;
      output[String(key)] = Math.round(parsed * 100) / 100;
    }
    return output;
  }
}
